/**
 * Unified chat state store — public re-export hub. Implementation is split
 * by concern:
 *
 *   - `chatStore.types.ts`     — interfaces
 *   - `chatStore.internal.ts`  — state, subscribe/emit/setState, helpers, input store
 *   - `chatStore.summarize.ts` — summarize-job polling
 *   - `chatStore.actions.ts`   — the `chatStore` mutator object
 *
 * Consumers import from this file only; the internal modules are private.
 */
import { useSyncExternalStore } from 'react';
import { chatStore } from './chatStore.actions.js';
import { createSelector, getInputSnapshot, getState, subscribe, subscribeInput } from './chatStore.internal.js';
import type { ChatState, ConversationSummary } from './chatStore.types.js';

// ─── Public hooks ────────────────────────────────────────────────────────────

/** Subscribe to the composer input value. Cheap — doesn't trigger main-state re-renders. */
export function useChatInput(): string {
  return useSyncExternalStore(subscribeInput, getInputSnapshot, getInputSnapshot);
}

/** Full state snapshot — prefer individual hooks for hot paths. */
export function useChatStore(): ChatState {
  return useSyncExternalStore(subscribe, getState, getState);
}

const selectConversations = createSelector((s) => s.conversations);

export function useChatConversationList(): ConversationSummary[] {
  return useSyncExternalStore(subscribe, selectConversations);
}

const selectConversationId = createSelector((s) => s.conversationId);

/** The open conversation's id — what its attachments are served under. */
export function useChatConversationId(): string {
  return useSyncExternalStore(subscribe, selectConversationId);
}

// ─── Public surface — the store object and the types consumers need ─────────

export { chatStore } from './chatStore.actions.js';
export type {
  ChatAttachment,
  ChatMessage,
  ConversationSummary,
  PendingQuestion,
  StreamSegment,
  SummarizeJob,
} from './chatStore.types.js';

// Boot-time hydration — the displayed default agent comes from Settings, not
// from a renderer constant. Side effect lives in the entry module so the
// import order (and the test environment without DOM/fetch) is unambiguous.
void chatStore.hydrateDefaults();
// Land on the conversation the operator was last in (localStorage bookmark),
// not a fresh empty thread. No-op when there is no bookmark or the thread
// was archived; conversation meta then re-pins provider/model over the
// hydrateDefaults values, so the two boot calls compose in either order.
void chatStore.restoreLastConversation();
